import React, { useState, useEffect, useRef } from 'react';
import { Button, notification, DatePicker } from 'antd';
import { Modal } from '_src/components';
import { Select, Input } from '_src/shared/form-helpers';
import { useSave } from 'store';
import services from '_src/services';
import moment from 'moment';

import { ModalContent, FormRow, Label } from '../styles';

const laptopConfig = services['laptopLending'];

const MAINTENANCE_TYPES = [
  { id: 'repair', label: 'Repair' },
  { id: 'cleaning', label: 'Cleaning' },
  { id: 'upgrade', label: 'Upgrade' },
  { id: 'inspection', label: 'Inspection' }
];

export default function MaintenanceLogModal({
  visible,
  onClose,
  laptop,
  onSaved
}) {
  const [form, setForm] = useState({
    maintenanceType: null,
    description: '',
    performedBy: '',
    cost: '',
    performedAt: moment(),
    nextMaintenanceDue: null
  });
  const [saving, setSaving] = useState(false);
  const laptopRef = useRef(null);

  const { save: addMaintenanceLog } = useSave(laptopConfig.addMaintenanceLog);

  useEffect(() => {
    if (visible && laptop) {
      laptopRef.current = laptop;
      setForm({
        maintenanceType: null,
        description: '',
        performedBy: '',
        cost: '',
        performedAt: moment(),
        nextMaintenanceDue: null
      });
    }
  }, [visible, laptop]);

  const currentLaptop = laptopRef.current;

  const handleSave = async () => {
    if (!currentLaptop?.id) {
      notification.error({ message: 'Invalid laptop selected', duration: 3 });
      return;
    }

    if (!form.maintenanceType) {
      notification.warning({ message: 'Please select a maintenance type', duration: 3 });
      return;
    }

    setSaving(true);

    const payload = {
      laptopId: currentLaptop.id,
      maintenance_type: form.maintenanceType,
      description: form.description,
      performed_by: form.performedBy,
      cost: form.cost ? parseFloat(form.cost) : null,
      performed_at: form.performedAt ? form.performedAt.toISOString() : null,
      next_maintenance_due: form.nextMaintenanceDue ? form.nextMaintenanceDue.format('YYYY-MM-DD') : null
    };

    const response = await addMaintenanceLog(payload);

    setSaving(false);

    if (response?.success === 1) {
      notification.success({ message: 'Maintenance log added successfully', duration: 5 });
      laptopRef.current = null;
      onSaved();
      onClose();
    } else {
      notification.error({
        message: response?.data?.message || 'Failed to add maintenance log',
        duration: 5
      });
    }
  };

  const laptopName = `${currentLaptop?.brand || ''} ${currentLaptop?.model || ''}`.trim();
  const serialNumber = currentLaptop?.serialNumber || currentLaptop?.serial_number || '';

  return (
    <Modal
      title="Add Maintenance Log"
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave} loading={saving}>
          Save Log
        </Button>
      ]}
      width={600}
    >
      <ModalContent>
        <FormRow>
          <Label>Laptop</Label>
          <div style={{ padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>
            {laptopName} {serialNumber && `(${serialNumber})`}
          </div>
        </FormRow>

        <FormRow>
          <Label>Maintenance Type *</Label>
          <Select
            placeholder="Select type"
            options={MAINTENANCE_TYPES}
            value={form.maintenanceType}
            onChange={value => setForm(prev => ({ ...prev, maintenanceType: value }))}
            style={{ width: '100%' }}
          />
        </FormRow>

        <FormRow>
          <Label>Description</Label>
          <Input
            name="description"
            placeholder="Describe the maintenance work performed"
            value={form.description}
            onChange={value => setForm(prev => ({ ...prev, description: value }))}
          />
        </FormRow>

        <FormRow>
          <Label>Performed By</Label>
          <Input
            name="performedBy"
            placeholder="Name of technician or vendor"
            value={form.performedBy}
            onChange={value => setForm(prev => ({ ...prev, performedBy: value }))}
          />
        </FormRow>

        <FormRow>
          <Label>Cost ($)</Label>
          <Input
            name="cost"
            placeholder="0.00"
            value={form.cost}
            onChange={value => setForm(prev => ({ ...prev, cost: value }))}
            type="number"
          />
        </FormRow>

        <FormRow>
          <Label>Date Performed</Label>
          <DatePicker
            style={{ width: '100%' }}
            value={form.performedAt}
            onChange={date => setForm(prev => ({ ...prev, performedAt: date }))}
            format="MM/DD/YYYY"
          />
        </FormRow>

        <FormRow>
          <Label>Next Maintenance Due</Label>
          <DatePicker
            style={{ width: '100%' }}
            value={form.nextMaintenanceDue}
            onChange={date => setForm(prev => ({ ...prev, nextMaintenanceDue: date }))}
            format="MM/DD/YYYY"
            placeholder="Optional"
          />
        </FormRow>
      </ModalContent>
    </Modal>
  );
}
